import { memo, useState } from 'react'
import { Box, Button, Chip, Typography } from '@mui/material'
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded'
import {
	getCategoryById,
	getYoutubeThumbnail,
} from '../config/helpVideos'
import { HELP_THEME } from '../utils/helpConstants'

function HelpVideoCard({ video, onWatch }) {
	const [imgLoaded, setImgLoaded] = useState(false)
	const [imgError, setImgError] = useState(false)
	const category = getCategoryById(video.categoryId)
	const thumbnail = getYoutubeThumbnail(video)

	return (
		<Box
			component='article'
			sx={{
				borderRadius: '16px',
				overflow: 'hidden',
				bgcolor: HELP_THEME.white,
				border: `1px solid ${HELP_THEME.border}`,
				boxShadow: '0 8px 22px rgba(15, 23, 42, 0.06)',
				display: 'flex',
				flexDirection: 'column',
				height: '100%',
				transition: 'transform 0.22s ease, box-shadow 0.22s ease',
				'&:hover': {
					transform: 'translateY(-4px)',
					boxShadow: '0 16px 32px rgba(67, 97, 238, 0.16)',
					borderColor: HELP_THEME.primaryBorder,
				},
			}}
		>
			<Box
				sx={{
					position: 'relative',
					pt: '56.25%',
					bgcolor: '#E0E7FF',
					overflow: 'hidden',
				}}
			>
				{thumbnail && !imgError ? (
					<Box
						component='img'
						src={thumbnail}
						alt=''
						loading='lazy'
						onLoad={() => setImgLoaded(true)}
						onError={() => setImgError(true)}
						sx={{
							position: 'absolute',
							inset: 0,
							width: '100%',
							height: '100%',
							objectFit: 'cover',
							opacity: imgLoaded ? 1 : 0,
							transition: 'opacity 0.25s ease',
						}}
					/>
				) : null}

				{!imgLoaded || imgError ? (
					<Box
						sx={{
							position: 'absolute',
							inset: 0,
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
							background: HELP_THEME.gradient,
							opacity: 0.9,
						}}
					>
						<Typography sx={{ fontSize: '2rem' }}>
							{category?.emoji || '🎥'}
						</Typography>
					</Box>
				) : null}

				<Box
					sx={{
						position: 'absolute',
						inset: 0,
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
						background:
							'linear-gradient(180deg, rgba(15,23,42,0.05) 0%, rgba(15,23,42,0.35) 100%)',
					}}
				>
					<Box
						sx={{
							width: 48,
							height: 48,
							borderRadius: '50%',
							bgcolor: 'rgba(255,255,255,0.95)',
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
							boxShadow: '0 8px 20px rgba(15,23,42,0.25)',
						}}
					>
						<PlayArrowRoundedIcon sx={{ color: HELP_THEME.primary, fontSize: 30 }} />
					</Box>
				</Box>

				{category ? (
					<Chip
						size='small'
						label={`${category.emoji ? `${category.emoji} ` : ''}${category.label}`}
						sx={{
							position: 'absolute',
							top: 10,
							left: 10,
							height: 26,
							fontSize: '0.68rem',
							fontWeight: 700,
							bgcolor: 'rgba(255,255,255,0.95)',
							color: HELP_THEME.text,
						}}
					/>
				) : null}

				{video.duration ? (
					<Chip
						size='small'
						label={video.duration}
						sx={{
							position: 'absolute',
							bottom: 10,
							right: 10,
							height: 24,
							fontSize: '0.68rem',
							fontWeight: 700,
							bgcolor: 'rgba(15,23,42,0.75)',
							color: '#fff',
						}}
					/>
				) : null}
			</Box>

			<Box sx={{ p: 2, display: 'flex', flexDirection: 'column', flex: 1, gap: 1 }}>
				<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
					<Typography sx={{ fontSize: '1.1rem', lineHeight: 1 }}>
						{category?.emoji || '🎥'}
					</Typography>
					<Typography
						sx={{
							fontWeight: 700,
							fontSize: '0.9375rem',
							color: HELP_THEME.text,
							lineHeight: 1.35,
						}}
					>
						{video.title}
					</Typography>
				</Box>
				<Typography
					sx={{
						fontSize: '0.8125rem',
						color: HELP_THEME.muted,
						lineHeight: 1.55,
						flex: 1,
					}}
				>
					{video.description}
				</Typography>
				<Button
					fullWidth
					variant='contained'
					onClick={() => onWatch?.(video)}
					startIcon={<PlayArrowRoundedIcon />}
					aria-label={`Watch ${video.title}`}
					sx={{
						mt: 0.5,
						textTransform: 'none',
						fontWeight: 700,
						borderRadius: '10px',
						py: 1,
						background: HELP_THEME.gradient,
						boxShadow: '0 6px 16px rgba(67, 97, 238, 0.28)',
						'&:hover': {
							background: HELP_THEME.gradientHover,
						},
					}}
				>
					Watch Video
				</Button>
			</Box>
		</Box>
	)
}

export default memo(HelpVideoCard)
